import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type Turnstile = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let loading: Promise<void> | null = null;

/** Loads the Turnstile script once (explicit render mode). */
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loading = null;
      reject(new Error("Turnstile failed to load"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export type TurnstileHandle = { reset: () => void };

type Props = {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  /** Bump to force a widget reset (e.g. after a failed request). */
  resetKey?: number;
  className?: string;
};

/** Cloudflare Turnstile, rendered explicitly. Render only when a site key is configured. */
export function TurnstileWidget({ siteKey, onVerify, onExpire, onError, resetKey = 0, className }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const handlers = useRef({ onVerify, onExpire, onError });
  useEffect(() => {
    handlers.current = { onVerify, onExpire, onError };
  });

  useEffect(() => {
    let cancelled = false;
    void loadTurnstile()
      .then(() => {
        if (cancelled || !window.turnstile || !el.current) return;
        widgetId.current = window.turnstile.render(el.current, {
          sitekey: siteKey,
          callback: (token: string) => handlers.current.onVerify(token),
          "expired-callback": () => handlers.current.onExpire?.(),
          "error-callback": () => handlers.current.onError?.(),
        });
      })
      .catch(() => handlers.current.onError?.());
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current !== null) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already gone */
        }
        widgetId.current = null;
      }
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetKey > 0 && window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
  }, [resetKey]);

  return <div ref={el} className={className} />;
}
